import Tabs from 'antd/lib/tabs';
import React, { useMemo, useState } from 'react';
import {
    ICreateSavedFilter,
    SavedFilterStatus,
    SavedFilterSubType,
    SavedFilterType,
} from '../../../../../components/sidebar-filters/saved-filter/interface';
import { VENDOR_BILL_ITEM_PAGE_CONST } from './constants';
import { SidebarFilterActions, TSidebarActions } from '../../../../../components/sidebar-filters/SidebarFilterActions';
import { useCustomizableTableContext } from '../../../../../components/table/CustomizableTableContext';
import { VendorBillItemsSidebarFilters } from './VendorBillItemSidebarFilters';
import { IVendorBillItemsSidebarFilters, useVendorBillItemsSidebarFiltersContext } from './VendorBillItemSidebarFiltersContext';
import { VendorBillItemsSidebarSaved } from './VendorBillItemsSidebarSaved';

type TSidebarTab = 'filters' | 'saved' | 'grid';

type TTabItem = {
    key: TSidebarTab;
    label: React.ReactNode;
    children: React.ReactNode;
};

interface IVendorBillItemSidebarTabsProps {
    onOpenSaveFiltersModal: (savedFilterData: ICreateSavedFilter | null) => void;
    onFilterChange: (filters: IVendorBillItemsSidebarFilters) => void;
    gridComponent: React.ReactNode;
}

export const VendorBillItemsSidebarTabs: React.FC<IVendorBillItemSidebarTabsProps> = ({ onOpenSaveFiltersModal, onFilterChange, gridComponent }) => {
    const [activeTab, setActiveTab] = useState<TSidebarTab>('filters');

    const handleTabChange = (key: string) => {
        setActiveTab(key as TSidebarTab);
    };

    const tabs: TTabItem[] = [
        {
            key: 'filters',
            label: <FiltersLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab} onOpenSaveFiltersModal={onOpenSaveFiltersModal}>
                    <VendorBillItemsSidebarFilters onFilterChange={onFilterChange} />
                </TabContentWrapper>
            ),
        },
        {
            key: 'grid',
            label: <GridLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab} onOpenSaveFiltersModal={onOpenSaveFiltersModal}>
                    {gridComponent}
                </TabContentWrapper>
            ),
        },
        {
            key: 'saved',
            label: <SavedLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab} onOpenSaveFiltersModal={onOpenSaveFiltersModal}>
                    <VendorBillItemsSidebarSaved onOpenSaveFiltersModal={onOpenSaveFiltersModal} />
                </TabContentWrapper>
            ),
        },
    ];

    return (
        <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            defaultActiveKey="filters"
            destroyOnHidden={false}
            items={tabs}
            tabBarStyle={{ marginBottom: 0, padding: '0px 5px' }}
        />
    );
};

// This components helps to less re-rendering and improve performances
const FiltersLabel: React.FC = () => {
    const { filtersCount } = useVendorBillItemsSidebarFiltersContext();

    return <span>Filters {filtersCount > 0 ? `(${filtersCount})` : ''}</span>;
};

const SavedLabel: React.FC = () => {
    const { savedFilters } = useVendorBillItemsSidebarFiltersContext();
    const savedFiltersCount = useMemo(() => savedFilters.length, [savedFilters]);

    return <span>Saved {savedFiltersCount > 0 ? `(${savedFiltersCount})` : ''}</span>;
};

const GridLabel: React.FC = () => {
    const { hiddenColumnsCount } = useCustomizableTableContext();

    return <span>Grid {hiddenColumnsCount > 0 ? `(${hiddenColumnsCount})` : ''}</span>;
};

interface ITabContentWrapperProps {
    children: React.ReactNode;
    activeTab: TSidebarTab;
    onOpenSaveFiltersModal: (savedFilterData: ICreateSavedFilter | null) => void;
}

const TabContentWrapper: React.FC<ITabContentWrapperProps> = ({ children, activeTab, onOpenSaveFiltersModal }) => {
    const { resetColumns } = useCustomizableTableContext();
    const { handleFiltersClear, filtersCount, filters } = useVendorBillItemsSidebarFiltersContext();

    const handleSaveFiltersClick = () => {
        onOpenSaveFiltersModal({
            id: null,
            filter_name: '',
            description: '',
            filter_values: filters,
            status: SavedFilterStatus.active,
            type: SavedFilterType.filter,
            sub_type: SavedFilterSubType.vendorBillItems,
        });
    };

    const shownActions: TSidebarActions[] = useMemo(() => {
        if (activeTab === 'filters') return ['saveFilters', 'clearFilters'];
        if (activeTab === 'saved') return ['clearFilters'];
        if (activeTab === 'grid') return ['resetGrid'];

        return [];
    }, [activeTab]);

    return (
        <div style={{ height: VENDOR_BILL_ITEM_PAGE_CONST.tabContentHeight, display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                }}
            >
                {children}
            </div>
            <SidebarFilterActions
                shownActions={shownActions}
                isSaveDisabled={filtersCount === 0}
                onSaveFilters={handleSaveFiltersClick}
                onClearFilters={handleFiltersClear}
                onResetGrid={resetColumns}
            />
        </div>
    );
};
